import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnorderedListOutlined
} from "@ant-design/icons"
import LinkExtension from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Button, Popover, Select } from "antd"
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../utils/cn.ts"
import {
  type AttachmentChipAttrs,
  AttachmentChip,
  ListIndentKeymap
} from "./rich-text-extensions.ts"

import "./rich-text-editor.css"
import { TextInput } from "../Input/TextInput.tsx"

interface RichTextEditorHandle {
  focus: () => void
  insertAttachmentChip: (attachment: AttachmentChipAttrs) => void
}

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  autoFocus?: boolean
  disabled?: boolean
  minHeight?: number
  /** A fully-built control (e.g. an antd `Upload`-wrapped button) so the
   * caller's upload/validation stays out of this generic editor. */
  attachButton?: React.ReactNode
  ariaLabel?: string
}

const BLOCK_NORMAL = "normal"
const BLOCK_HEADING = "heading"

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    {
      value,
      onChange,
      placeholder,
      autoFocus,
      disabled,
      minHeight = 220,
      attachButton,
      ariaLabel
    },
    ref
  ) {
    const { t } = useTranslation()
    const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
    const [linkValue, setLinkValue] = useState("")

    const extensions = useMemo(
      () => [
        StarterKit.configure({
          heading: { levels: [2] },
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
          code: false,
          strike: false
        }),
        LinkExtension.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          // No `class` — the sanitizer strips it from saved HTML (see
          // rich-text-html.ts), so the look is keyed off `a[href]` in CSS.
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer nofollow"
          }
        }),
        Placeholder.configure({ placeholder: placeholder ?? "" }),
        AttachmentChip,
        ListIndentKeymap
      ],
      // Changing `placeholder` after mount is not a supported live-update path
      // for this editor — every caller sets it once, from a stable i18n key.
      []
    )

    const editor = useEditor({
      extensions,
      content: value,
      editable: !disabled,
      autofocus: autoFocus ? "end" : false,
      onUpdate: ({ editor: current }) => onChange(current.getHTML()),
      editorProps: {
        attributes: {
          role: "textbox",
          "aria-multiline": "true",
          ...(ariaLabel ? { "aria-label": ariaLabel } : {})
        }
      }
    })

    // `useEditor`'s `editable` option is only read at creation — without
    // this, the contentEditable region stayed live while a save was in
    // flight (WorkspaceCard sets `disabled` via `saving`), so a keystroke
    // during that window was never part of the already-captured payload.
    // `setEditable`'s second argument must be `false`: it defaults to `true`
    // and, undocumented, that means "emit an `update` event" — which fired
    // `onChange` on mount with nothing actually typed, making a legacy
    // plain-text note (re-serialized as `<p>...</p>` by the round trip)
    // register as dirty before the user touched anything.
    useEffect(() => {
      editor?.setEditable(!disabled, false)
    }, [editor, disabled])

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.chain().focus().run(),
        // An upload resolves asynchronously — the user is often still typing
        // elsewhere by the time it does, so this appends at the end without
        // `updateSelection`, which would otherwise yank their cursor away
        // from wherever they currently are.
        insertAttachmentChip: (attachment) =>
          editor
            ?.chain()
            .insertContentAt(
              editor.state.doc.content.size,
              [
                {
                  type: "paragraph",
                  content: [{ type: "attachmentChip", attrs: attachment }]
                }
              ],
              { updateSelection: false }
            )
            .run()
      }),
      [editor]
    )

    if (!editor) return null

    const blockValue = editor.isActive("heading", { level: 2 })
      ? BLOCK_HEADING
      : BLOCK_NORMAL

    const setBlockValue = (next: string) => {
      const chain = editor.chain().focus()
      if (next === BLOCK_HEADING) chain.setHeading({ level: 2 }).run()
      else chain.setParagraph().run()
    }

    const openLinkPopover = (open: boolean) => {
      if (open) setLinkValue(editor.getAttributes("link").href ?? "")
      setLinkPopoverOpen(open)
    }

    const applyLink = () => {
      const url = linkValue.trim()
      if (!url) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run()
      } else if (editor.state.selection.empty) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: url,
            marks: [{ type: "link", attrs: { href: url } }]
          })
          .run()
      } else {
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
      }
      setLinkPopoverOpen(false)
    }

    const toolbarButton = (
      active: boolean,
      onClick: () => void,
      icon: React.ReactNode,
      label: string
    ) => (
      <Button
        type={"text"}
        size={"small"}
        aria-label={label}
        aria-pressed={active}
        disabled={disabled}
        className={cn(active && "bg-primary-bg text-primary")}
        // A plain button naturally steals DOM focus/selection from the
        // contentEditable on click; preventing mousedown's default keeps
        // the editor's selection intact so a toggle over selected text
        // applies to that selection rather than a collapsed cursor.
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
      >
        {icon}
      </Button>
    )

    return (
      <div
        className={cn(
          "rich-text-editor rounded-md border border-border overflow-hidden",
          disabled && "opacity-70"
        )}
      >
        <div
          className={
            "flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-border bg-muted"
          }
        >
          <Select
            size={"small"}
            variant={"borderless"}
            value={blockValue}
            disabled={disabled}
            className={"w-[92px]"}
            popupMatchSelectWidth={false}
            options={[
              { value: BLOCK_NORMAL, label: t("richTextEditor.toolbar.normal") },
              { value: BLOCK_HEADING, label: t("richTextEditor.toolbar.heading") }
            ]}
            onChange={setBlockValue}
          />
          <span className={"w-px h-4 bg-border mx-1"} />
          {toolbarButton(
            editor.isActive("bold"),
            () => editor.chain().focus().toggleBold().run(),
            <BoldOutlined />,
            t("richTextEditor.toolbar.bold")
          )}
          {toolbarButton(
            editor.isActive("italic"),
            () => editor.chain().focus().toggleItalic().run(),
            <ItalicOutlined />,
            t("richTextEditor.toolbar.italic")
          )}
          <span className={"w-px h-4 bg-border mx-1"} />
          {toolbarButton(
            editor.isActive("bulletList"),
            () => editor.chain().focus().toggleBulletList().run(),
            <UnorderedListOutlined />,
            t("richTextEditor.toolbar.bulletList")
          )}
          {toolbarButton(
            editor.isActive("orderedList"),
            () => editor.chain().focus().toggleOrderedList().run(),
            <OrderedListOutlined />,
            t("richTextEditor.toolbar.orderedList")
          )}
          <span className={"w-px h-4 bg-border mx-1"} />
          <Popover
            trigger={"click"}
            open={linkPopoverOpen}
            onOpenChange={openLinkPopover}
            placement={"bottomLeft"}
            content={
              <div className={"flex items-center gap-2 w-64"}>
                <TextInput
                  disableAutoCapitalize
                  size={"small"}
                  autoFocus
                  placeholder={t("richTextEditor.link.placeholder")}
                  value={linkValue}
                  onChange={(event) => setLinkValue(event.target.value)}
                  onPressEnter={applyLink}
                />
                <Button size={"small"} type={"primary"} onClick={applyLink}>
                  {t("richTextEditor.link.apply")}
                </Button>
              </div>
            }
          >
            {toolbarButton(
              editor.isActive("link"),
              () => undefined,
              <LinkOutlined />,
              t("richTextEditor.toolbar.link")
            )}
          </Popover>
          {attachButton}
        </div>

        <EditorContent
          editor={editor}
          className={"rich-text-content px-3 py-2 overflow-y-auto tailwind-scroll"}
          style={{ minHeight }}
        />
      </div>
    )
  }
)

export { type RichTextEditorHandle, type RichTextEditorProps, RichTextEditor }
